// ── Pricing tables ────────────────────────────────────────────────
// Defaults — overridden by rows in the service_prices table when
// available. `pricingOverride` argument to helpers below carries the
// DB-loaded shape; helpers fall back to these constants per-frequency.
export const MONTHLY_PRICING = {
  weekly:         { 1:95.33,  2:108.33, 3:121.24, 4:138.67 },
  biweekly:       { 1:73.67,  2:81.25,  3:91.00,  4:108.33 },
  "twice-weekly": { 1:162.50, 2:179.83, 3:201.50, 4:225.33 },
};
// Cleanup visits per month by cadence — the divisor for deriving a per-visit
// rate from the monthly price.
export const VISITS_PER_MONTH = { weekly:4.33, biweekly:2.17, "twice-weekly":8.66 };
// B18: per-visit is DERIVED from the monthly table (round(monthly / visits)) so
// it's always internally consistent with it — the old hardcoded table drifted a
// few cents (e.g. biweekly 4-dog 49.92 vs 50.00) and disagreed with the backend.
export const PER_VISIT_PRICING = Object.fromEntries(
  Object.entries(MONTHLY_PRICING).map(([freq, dogsMap]) => [
    freq,
    Object.fromEntries(Object.entries(dogsMap).map(([d, price]) =>
      [d, Math.round((price / (VISITS_PER_MONTH[freq] || 4.33)) * 100) / 100])),
  ])
);
// Deo (deodorizer) per-visit rates
export const DEO_PER_VISIT = { weekly:10, biweekly:15, monthly:20 };
// Deo visits-per-month (used to derive monthly cost from per-visit rate)
export const DEO_VISITS_PER_MONTH = { weekly:4.33, biweekly:2.17, monthly:1 };

export const getDeoMonthly = (freq, pricingOverride) => {
  const rate = pricingOverride?.deoPerVisit?.[freq] ?? DEO_PER_VISIT[freq] ?? 0;
  return Math.round(rate * (DEO_VISITS_PER_MONTH[freq]||1) * 100) / 100;
};
// Overlay the org's saved price ON the platform default PER CELL — not per table.
// An operator who set only some freq/dog combos previously saw $0 for the rest
// (the UI showed `?? 0`) while create-customer billed the platform default (B7),
// so a quoted-from-screen price silently mismatched the charge. Now the screen
// shows the exact amount that will be billed for every combo.
export const getPrice    = (freq, dogs, pricingOverride) => pricingOverride?.monthly?.[freq]?.[Math.min(Number(dogs),4)]  ?? MONTHLY_PRICING[freq]?.[Math.min(Number(dogs),4)]  ?? 0;
export const getPerVisit = (freq, dogs, pricingOverride) => pricingOverride?.perVisit?.[freq]?.[Math.min(Number(dogs),4)] ?? PER_VISIT_PRICING[freq]?.[Math.min(Number(dogs),4)] ?? 0;
// B13: forward pricingOverride to BOTH helpers — previously it was dropped, so
// an operator with custom prices saw default rates in the new-client total.
export const getTotalMonthly = (freq, dogs, deoEnabled, deoFreq, pricingOverride) =>
  getPrice(freq, dogs, pricingOverride) +
  (deoEnabled && deoFreq ? getDeoMonthly(deoFreq, pricingOverride) : 0);

// Allowed deo frequencies based on cleanup frequency
export const DEO_FREQ_OPTIONS = {
  "twice-weekly": ["weekly", "biweekly", "monthly"],
  "weekly":       ["weekly", "biweekly", "monthly"],
  "biweekly":     ["biweekly", "monthly"],
};
